import { useState } from "react";
import { ArrowDown, ArrowUp, Pencil, Plus, Power, PowerOff } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import {
  STALLED_THRESHOLD_KEY,
  STALLED_THRESHOLD_RANGE,
  type AdminStage,
  type ManagerColor,
  type OpportunityStatus,
} from "@salesboard/contracts";
import { useDocumentTitle } from "@/app/useDocumentTitle";
import { useT, type Translator } from "@/i18n";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { RowActions, type RowAction } from "@/components/ui/RowActions";
import { StatusChip } from "@/components/ui/StatusChip";
import { Tabs, type TabItem } from "@/components/ui/Tabs";
import { FormDialog } from "@/components/ui/FormDialog";
import { Field, Input, Select } from "@/components/ui/input";
import { ColourPicker, Swatch } from "@/components/ui/ColourPicker";
import { ErrorState, LoadingState } from "@/components/ui/states";
import { ApiClientError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

import {
  useAdminStages,
  useCreateStage,
  useRenameStage,
  useReorderStages,
  useSetStageActive,
  useSetStageColor,
  useSetStageType,
  useSettings,
  useUpdateSetting,
} from "./hooks.stages";

/**
 * The three "only … stage" refusals, as whole sentences rather than one sentence with the
 * type interpolated: in Czech the type is an adjective that has to agree with *fáze*, which is
 * not something a `${}` can do.
 */
const ONLY_STAGE_KEYS = {
  open: "settings.stageBlockedOnlyOpen",
  won: "settings.stageBlockedOnlyWon",
  lost: "settings.stageBlockedOnlyLost",
} as const satisfies Record<OpportunityStatus, string>;

/**
 * Why this stage cannot be changed, in the words the API would use — or nothing.
 *
 * The server derives `canChangeType`/`canDeactivate`; this only says *which* of the two rules
 * is in the way, which is presentation.
 *
 * **It is carried by the action it refuses** (2026-08-26). It used to print under the stage's
 * name, and the count it quotes had a column of its own — a permanent sentence, and a permanent
 * number, about a rule that only matters at the moment you try to break it. Inside the menu it
 * reaches the reader where their attention already is, and Radix keeps a disabled item
 * focusable, so unlike a disabled *button* the reason stays reachable by keyboard.
 */
function blockedReason(t: Translator, stage: AdminStage): string | null {
  if (stage.canChangeType || stage.canDeactivate) return null;
  if (stage.opportunityCount > 0) {
    return t("settings.stageBlockedHolds", t("count.opportunities", stage.opportunityCount));
  }
  return t(ONLY_STAGE_KEYS[stage.closedType]);
}

/** The tab keys, in the URL so a settings link opens where the sender was. */
const SETTINGS_TABS = ["stages", "stalled"] as const;
type SettingsTab = (typeof SETTINGS_TABS)[number];
const SETTINGS_TAB_PARAM = "settings:tab";

/**
 * Application configuration: the shape of the board, and the one knob that changes what it
 * means.
 *
 * **Two tabs rather than two stacked sections** (2026-08-26). Stacked, the stalled threshold —
 * a single number that changes what "stalled" means on every board — sat below a table long
 * enough to scroll past, and read as a footnote to the stages. As tabs they are two equal
 * subjects, and the active one is in the URL like every other tab in the product.
 *
 * The heading is **visible** here, unlike the `sr-only` ones on the list screens: those have a
 * toolbar naming what they are, and this is the one screen reached from a menu rather than from
 * the nav — arriving with no title at all left the reader guessing where the menu had taken
 * them.
 */
export function SettingsPage(): React.JSX.Element {
  const t = useT();
  useDocumentTitle(t("settings.title"));
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get(SETTINGS_TAB_PARAM);
  const active: SettingsTab = SETTINGS_TABS.find((tab) => tab === raw) ?? "stages";

  const tabs: TabItem<SettingsTab>[] = [
    { value: "stages", label: t("settings.stagesTitle") },
    { value: "stalled", label: t("settings.thresholdTitle") },
  ];

  return (
    <>
      <h1 className="mb-4 text-xl font-semibold">{t("settings.title")}</h1>

      <Tabs
        label={t("settings.tabsLabel")}
        tabs={tabs}
        active={active}
        idPrefix="settings"
        onChange={(next) => {
          const params = new URLSearchParams(window.location.search);
          // Deviations only, as everywhere else: the default tab leaves no parameter behind.
          if (next === "stages") params.delete(SETTINGS_TAB_PARAM);
          else params.set(SETTINGS_TAB_PARAM, next);
          setSearchParams(params, { replace: true });
        }}
      >
        {active === "stages" ? <StagesSection /> : <StalledThresholdSection />}
      </Tabs>
    </>
  );
}

function StagesSection(): React.JSX.Element {
  const t = useT();
  const query = useAdminStages();
  const create = useCreateStage();
  const rename = useRenameStage();
  const setType = useSetStageType();
  const setColor = useSetStageColor();
  const setActive = useSetStageActive();
  const reorder = useReorderStages();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AdminStage>();
  const [deactivating, setDeactivating] = useState<AdminStage>();
  const [reactivating, setReactivating] = useState<AdminStage>();
  const [reordering, setReordering] = useState<{ stages: AdminStage[]; moved: AdminStage }>();

  const stages = query.data ?? [];
  const active = stages.filter((stage) => stage.active);

  /** Swap two active stages and confirm, because the board's order is everyone's. */
  const move = (stage: AdminStage, direction: -1 | 1): void => {
    const index = active.findIndex((candidate) => candidate.id === stage.id);
    const target = index + direction;
    if (target < 0 || target >= active.length) return;

    const next = [...active];
    next[index] = active[target]!;
    next[target] = stage;
    setReordering({ stages: next, moved: stage });
  };

  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-3">
        {/* No heading: the tab above is the heading, and repeating it under itself was the
            second-largest text on the screen saying nothing new. */}
        <p className="text-muted-foreground text-sm">{t("settings.stagesDescription")}</p>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          {t("settings.newStage")}
        </Button>
      </div>

      {query.isPending && <LoadingState rows={6} label={t("settings.stagesLoading")} />}

      {query.error && (
        <ErrorState
          message={query.error instanceof ApiClientError ? query.error.message : t("settings.stagesFailed")}
          requestId={query.error instanceof ApiClientError ? query.error.requestId : "unknown"}
          onRetry={() => void query.refetch()}
        />
      )}

      {query.data && (
        <div className="bg-card overflow-hidden rounded-lg border">
          <table className="w-full text-sm">
            <caption className="sr-only">{t("settings.stagesCaption")}</caption>
            <thead className="bg-muted/50 text-muted-foreground text-xs">
              <tr>
                <th className="px-4 py-2 text-left font-medium">{t("column.order")}</th>
                <th className="px-4 py-2 text-left font-medium">{t("settings.stageName")}</th>
                <th className="px-4 py-2 text-left font-medium">{t("column.type")}</th>
                <th className="px-4 py-2 text-left font-medium">{t("column.colour")}</th>
                <th className="px-4 py-2 text-left font-medium">{t("column.state")}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {stages.map((stage) => {
                const blocked = blockedReason(t, stage);
                return (
                <tr key={stage.id} className="border-t">
                  <td className="text-muted-foreground px-4 py-2 tabular-nums">
                    {/* An inactive stage keeps a number nobody can see on the board, so it
                        shows none rather than one that means nothing. */}
                    {stage.active ? stage.displayOrder : "—"}
                  </td>
                  <td className="px-4 py-2 font-medium">{stage.name}</td>
                  <td className="px-4 py-2">{t(`status.${stage.closedType}`)}</td>
                  <td className="px-4 py-2">
                    {/* The colour as a fact, not a control (2026-08-26). A picker in every row
                        made the table a form; setting one now happens in the dialog, exactly
                        as it does for a manager's colour on the Users page. What a reader
                        needs here is which colour, which the swatch and the word both say. */}
                    <span className="flex items-center gap-2 text-sm">
                      <Swatch color={stage.color} />
                      {t(stage.color ? `colour.${stage.color}` : "colour.none")}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {/* The shared chip, as on clients: two states, green or grey, and the word
                        carries the state so colour is never the only signal. */}
                    <StatusChip
                      on={stage.active}
                      label={stage.active ? t("settings.stageActive") : t("settings.stageInactive")}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex justify-end">
                      <StageActions
                        stage={stage}
                        blocked={blocked}
                        first={active[0]?.id === stage.id}
                        last={active.at(-1)?.id === stage.id}
                        reordering={reorder.isPending}
                        onMove={move}
                        onEdit={() => setEditing(stage)}
                        onDeactivate={() => setDeactivating(stage)}
                        onReactivate={() => setReactivating(stage)}
                      />
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && (
        <StageFormDialog
          title={t("settings.newStage")}
          submitLabel={t("settings.addStage")}
          pending={create.isPending}
          onClose={() => setCreateOpen(false)}
          onSubmit={(name, closedType) => {
            create.mutate({ name, closedType });
            setCreateOpen(false);
          }}
        />
      )}

      {editing && (
        <StageEditDialog
          stage={editing}
          pending={rename.isPending || setType.isPending || setColor.isPending}
          onClose={() => setEditing(undefined)}
          onSubmit={async (name, closedType, color) => {
            // Two routes, each carrying a version, so the second write uses the version the
            // first one returned — sending the row's original twice would be refused as stale.
            let version = editing.version;
            if (name !== editing.name) {
              // These routes answer with the whole ordered list, so the new version is read
              // from this stage's row inside it.
              const stages = await rename.mutateAsync({ id: editing.id, name, version });
              version = stages.find((row) => row.id === editing.id)?.version ?? version + 1;
            }
            if (closedType !== editing.closedType) {
              const stages = await setType.mutateAsync({ id: editing.id, closedType, version });
              version = stages.find((row) => row.id === editing.id)?.version ?? version + 1;
            }
            if (color !== editing.color) {
              await setColor.mutateAsync({ id: editing.id, color, version });
            }
            setEditing(undefined);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(deactivating)}
        onOpenChange={(next) => !next && setDeactivating(undefined)}
        title={deactivating ? t("settings.deactivateStageTitle", deactivating.name) : ""}
        consequence={t("settings.deactivateStageConsequence")}
        confirmLabel={t("settings.deactivateStageConfirm")}
        destructive
        pending={setActive.isPending}
        onConfirm={() => {
          if (deactivating) {
            setActive.mutate({ id: deactivating.id, active: false, version: deactivating.version });
          }
          setDeactivating(undefined);
        }}
      />

      <ConfirmDialog
        open={Boolean(reactivating)}
        onOpenChange={(next) => !next && setReactivating(undefined)}
        title={reactivating ? t("settings.reactivateStageTitle", reactivating.name) : ""}
        // Confirmed like every other change to the board's shape: it adds a row to
        // everyone's board, at the end of its block.
        consequence={t("settings.reactivateStageConsequence")}
        confirmLabel={t("settings.reactivateStageConfirm")}
        pending={setActive.isPending}
        onConfirm={() => {
          if (reactivating) setActive.mutate({ id: reactivating.id, active: true, version: reactivating.version });
          setReactivating(undefined);
        }}
      />

      <ConfirmDialog
        open={Boolean(reordering)}
        onOpenChange={(next) => !next && setReordering(undefined)}
        title={reordering ? t("settings.reorderTitle", reordering.moved.name) : ""}
        // Named because it is not a personal preference: the board's row order is shared.
        consequence={t("settings.reorderConsequence")}
        confirmLabel={t("settings.reorderConfirm")}
        pending={reorder.isPending}
        onConfirm={() => {
          if (reordering) {
            reorder.mutate({
              stages: reordering.stages.map((stage) => ({ id: stage.id, version: stage.version })),
            });
          }
          setReordering(undefined);
        }}
      />
    </section>
  );
}

/**
 * One stage's actions, behind one trigger.
 *
 * The row carried five buttons — two arrows, rename, change type, deactivate — which made every
 * line a toolbar and left no width for the columns carrying facts (2026-08-26). Reordering
 * confirms before it writes either way, so a menu costs it nothing: it was never a one-click
 * operation.
 */
function StageActions({
  stage,
  blocked,
  first,
  last,
  reordering,
  onMove,
  onEdit,
  onDeactivate,
  onReactivate,
}: {
  stage: AdminStage;
  /** Why nothing about this stage can change, if that is the case. */
  blocked: string | null;
  first: boolean;
  last: boolean;
  reordering: boolean;
  onMove: (stage: AdminStage, direction: -1 | 1) => void;
  onEdit: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
}): React.JSX.Element {
  const t = useT();
  const actions: RowAction[] = [{ label: t("common.edit"), icon: Pencil, onSelect: onEdit }];

  if (stage.active) {
    actions.push(
      {
        label: t("settings.moveUp", stage.name),
        icon: ArrowUp,
        disabledReason: first ? t("settings.alreadyFirst") : reordering ? t("common.saving") : undefined,
        onSelect: () => onMove(stage, -1),
      },
      {
        label: t("settings.moveDown", stage.name),
        icon: ArrowDown,
        disabledReason: last ? t("settings.alreadyLast") : reordering ? t("common.saving") : undefined,
        onSelect: () => onMove(stage, 1),
      },
      {
        label: t("common.deactivate"),
        icon: PowerOff,
        destructive: true,
        // The server derived this; the reason is the one the API would have returned.
        disabledReason: stage.canDeactivate ? undefined : (blocked ?? t(ONLY_STAGE_KEYS[stage.closedType])),
        onSelect: onDeactivate,
      },
    );
  } else {
    actions.push({ label: t("common.reactivate"), icon: Power, onSelect: onReactivate });
  }

  return <RowActions label={t("settings.stageRowActions", stage.name)} actions={actions} />;
}

/**
 * Create a stage: its name, and which kind of stage it is.
 *
 * Editing has its own dialog below — the two used to share this one, with the type select
 * hidden for a rename, which meant the screen had a *rename* dialog and a separate *change
 * type* dialog for what a reader thinks of as one thing.
 */
function StageFormDialog({
  title,
  submitLabel,
  pending,
  onClose,
  onSubmit,
}: {
  title: string;
  submitLabel: string;
  pending: boolean;
  onClose: () => void;
  onSubmit: (name: string, closedType: OpportunityStatus) => void;
}) {
  const t = useT();
  const [name, setName] = useState("");
  const [closedType, setClosedType] = useState<OpportunityStatus>("open");

  return (
    <FormDialog
      open
      onOpenChange={(next) => !next && onClose()}
      title={title}
      submitLabel={submitLabel}
      pending={pending}
      submitDisabled={name.trim() === ""}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(name.trim(), closedType);
      }}
    >
      <Field label={t("settings.stageName")} htmlFor="stage-name">
        <Input id="stage-name" value={name} onChange={(event) => setName(event.target.value)} />
      </Field>

      <Field label={t("column.type")} htmlFor="stage-type" hint={t("settings.stageTypeHint")}>
        <Select
          id="stage-type"
          value={closedType}
          onChange={(event) => setClosedType(event.target.value as OpportunityStatus)}
        >
          <option value="open">{t("settings.stageTypeOpen")}</option>
          <option value="won">{t("settings.stageTypeWon")}</option>
          <option value="lost">{t("settings.stageTypeLost")}</option>
        </Select>
      </Field>
    </FormDialog>
  );
}

/**
 * Edit a stage: its name, its type, and the colour it paints its board row.
 *
 * A reader who renamed a stage and then wanted it to count as *won* had to find a second
 * action in a second dialog; all three are attributes of the stage, so all three are here.
 *
 * Colour is **not** on the create dialog, unlike a manager's: `createStageSchema` carries a
 * name and a type, and colour has a route of its own. Giving create a colour would mean either
 * widening that contract or issuing a second write that can half-fail — neither of which this
 * batch asked for. A new stage is one Edit away from its colour.
 *
 * A reader who renamed a stage and then wanted it to count as *won* had to find a second
 * action in a second dialog; both are attributes of the stage, so both are here. The type
 * select is disabled when the server says the change is impossible, carrying the reason — a
 * name change is never blocked, so the dialog still has something to do in that state.
 */
function StageEditDialog({
  stage,
  pending,
  onClose,
  onSubmit,
}: {
  stage: AdminStage;
  pending: boolean;
  onClose: () => void;
  onSubmit: (name: string, closedType: OpportunityStatus, color: ManagerColor | null) => Promise<void>;
}) {
  const t = useT();
  const [name, setName] = useState(stage.name);
  const [closedType, setClosedType] = useState<OpportunityStatus>(stage.closedType);
  const [color, setColor] = useState<ManagerColor | null>(stage.color);
  const blocked = blockedReason(t, stage);

  const trimmed = name.trim();
  const dirty =
    (trimmed !== stage.name && trimmed !== "") || closedType !== stage.closedType || color !== stage.color;

  return (
    <FormDialog
      open
      onOpenChange={(next) => !next && onClose()}
      title={t("settings.editStageTitle", stage.name)}
      submitLabel={t("common.saveChanges")}
      pending={pending}
      submitDisabled={!dirty}
      onSubmit={(event) => {
        event.preventDefault();
        void onSubmit(trimmed, closedType, color);
      }}
    >
      <Field label={t("settings.stageName")} htmlFor="edit-stage-name">
        <Input id="edit-stage-name" value={name} onChange={(event) => setName(event.target.value)} />
      </Field>

      <Field
        label={t("column.type")}
        htmlFor="edit-stage-type"
        // Editable: what a type actually decides. Blocked: why it cannot be changed.
        hint={
          stage.canChangeType
            ? t("settings.stageTypeDialogDescription")
            : (blocked ?? t(ONLY_STAGE_KEYS[stage.closedType]))
        }
      >
        <Select
          id="edit-stage-type"
          value={closedType}
          disabled={!stage.canChangeType}
          onChange={(event) => setClosedType(event.target.value as OpportunityStatus)}
        >
          <option value="open">{t("settings.stageTypeOpen")}</option>
          <option value="won">{t("settings.stageTypeWon")}</option>
          <option value="lost">{t("settings.stageTypeLost")}</option>
        </Select>
      </Field>

      {/* Decoration, so it needs no guard: unlike a type change or a deactivation, recolouring
          a stage cannot make an opportunity's status underivable. It is still audited — the
          board it repaints is shared. */}
      <Field label={t("column.colour")} htmlFor="edit-stage-colour">
        <ColourPicker
          id="edit-stage-colour"
          label={t("colour.forStage", stage.name)}
          triggerLabel={t(color ? `colour.${color}` : "colour.none")}
          value={color}
          onChoose={setColor}
        />
      </Field>

      {closedType !== stage.closedType && (
        <p className="text-muted-foreground text-xs">
          {closedType === "open" ? t("settings.stageMovesToOpen") : t("settings.stageMovesToClosed")}
        </p>
      )}
    </FormDialog>
  );
}

function StalledThresholdSection(): React.JSX.Element {
  const t = useT();
  const query = useSettings();
  const update = useUpdateSetting();
  const setting = query.data?.find((row) => row.key === STALLED_THRESHOLD_KEY);

  const [draft, setDraft] = useState<string>();
  const value = draft ?? setting?.value ?? "";
  // Digits only, mirroring the server. A `type="number"` input happily yields "3e1" and
  // "30.0", which pass a `Number.isInteger` check and then fail the `::int` cast inside the
  // derivation view — taking every board read down with them.
  const days = Number(value.trim());
  const valid =
    /^\d+$/.test(value.trim()) && days >= STALLED_THRESHOLD_RANGE.min && days <= STALLED_THRESHOLD_RANGE.max;

  return (
    <section>
      <p className="text-muted-foreground mb-3 text-sm">{t("settings.thresholdDescription")}</p>

      {query.isPending && <LoadingState rows={2} label={t("settings.thresholdLoading")} />}

      {query.error && (
        <ErrorState
          message={query.error instanceof ApiClientError ? query.error.message : t("settings.thresholdFailed")}
          requestId={query.error instanceof ApiClientError ? query.error.requestId : "unknown"}
          onRetry={() => void query.refetch()}
        />
      )}

      {setting && (
        <form
          className="bg-card max-w-xl space-y-3 rounded-lg border p-4"
          onSubmit={(event) => {
            event.preventDefault();
            update.mutate({ key: setting.key, value, version: setting.version });
            setDraft(undefined);
          }}
        >
          <div className="space-y-1.5">
            <label htmlFor="stalled-threshold" className="text-sm font-medium">
              {t("settings.thresholdDays")}
            </label>
            <Input
              id="stalled-threshold"
              type="number"
              inputMode="numeric"
              min={STALLED_THRESHOLD_RANGE.min}
              max={STALLED_THRESHOLD_RANGE.max}
              value={value}
              onChange={(event) => setDraft(event.target.value)}
              className="w-32"
            />
          </div>

          {!valid && (
            <p role="alert" className="text-destructive text-sm">
              {t("settings.thresholdRange", STALLED_THRESHOLD_RANGE.min, STALLED_THRESHOLD_RANGE.max)}
            </p>
          )}

          {valid && days < STALLED_THRESHOLD_RANGE.warnBelow && (
            // A warning, not a refusal: it is a legal value that will mark a great deal of
            // healthy work as stalled, which is worth saying before it happens.
            <p className="text-warning text-sm">
              {t("settings.thresholdWarn", STALLED_THRESHOLD_RANGE.warnBelow)}
            </p>
          )}

          <p className="text-muted-foreground text-xs">
            {t("settings.thresholdRetroPrefix")} <span className="font-medium">{t("settings.thresholdRetroEmphasis")}</span>
            {t("settings.thresholdRetroSuffix")}
          </p>

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={!valid || update.isPending || value === setting.value}>
              {update.isPending ? t("common.saving") : t("settings.thresholdSave")}
            </Button>
            <span className="text-muted-foreground text-xs">
              {setting.modifiedByName
                ? t("settings.thresholdLastChangedBy", formatDateTime(setting.modifiedAt), setting.modifiedByName)
                : t("settings.thresholdLastChanged", formatDateTime(setting.modifiedAt))}
            </span>
          </div>
        </form>
      )}
    </section>
  );
}
